import { useState } from 'react'
import { CheckCircle, XCircle, SkipForward, AlertCircle } from 'lucide-react'
import type { BulkUploadToClientsResponse, VendorUploadResult, DocumentGenerationResult } from '@common/types'
import { DRATA_UPLOAD_ERROR_LABELS } from '@common/utils/drataErrors'
import { bulkUploadVendorsToClientsApi } from '@frontend/lib/api'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/components/ui/table'
import {
  BulkUploadToClientsDialogBase,
  type ClientSelection,
} from '@frontend/components/shared/BulkUploadToClientsDialogBase'

interface BulkUploadToClientsDialogProps {
  vendorIds: string[]
  vendorNames: Map<string, string>
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}

export function BulkUploadToClientsDialog({
  vendorIds,
  vendorNames,
  open,
  onOpenChange,
  onComplete,
}: BulkUploadToClientsDialogProps) {
  const [uploadToDrata, setUploadToDrata] = useState(true)

  const submit = async (clients: ClientSelection[]) => {
    return bulkUploadVendorsToClientsApi({ vendorIds, clients, uploadToDrata })
  }

  return (
    <BulkUploadToClientsDialogBase<BulkUploadToClientsResponse>
      open={open}
      onOpenChange={onOpenChange}
      onComplete={onComplete}
      title="Upload to Clients"
      description={`Generate compliance documents for ${vendorIds.length} vendor${
        vendorIds.length !== 1 ? 's' : ''
      } and upload to selected clients`}
      allowCreateClients
      submit={submit}
      invalidateOnSuccess={[['vendors']]}
      submittingMessage={`Generating documents for ${vendorIds.length} vendor${vendorIds.length !== 1 ? 's' : ''}...`}
      selectedItems={vendorIds.map((id) => ({ id, name: vendorNames.get(id) || id }))}
      selectedItemsLabel="Selected Vendors"
      formExtras={
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={uploadToDrata}
              onChange={(e) => setUploadToDrata(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm font-medium">Upload to Drata</span>
          </label>
          <p className="text-xs text-muted-foreground ml-6">
            Automatically upload generated PDFs to Drata for clients with API keys
          </p>
        </div>
      }
      renderResults={(results) => <VendorResultsBody results={results} />}
    />
  )
}

function VendorResultsBody({ results }: { results: BulkUploadToClientsResponse }) {
  return (
    <>
      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
        {results.overallSummary.vendorsSucceeded > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-500" />
            <span className="text-sm font-medium">
              {results.overallSummary.documentsSucceeded} document
              {results.overallSummary.documentsSucceeded !== 1 ? 's' : ''} generated
            </span>
          </div>
        )}
        {results.overallSummary.documentsFailed > 0 && (
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium">{results.overallSummary.documentsFailed} failed</span>
          </div>
        )}
        {results.overallSummary.vendorsSkipped > 0 && (
          <div className="flex items-center gap-2">
            <SkipForward className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium">
              {results.overallSummary.vendorsSkipped} vendor
              {results.overallSummary.vendorsSkipped !== 1 ? 's' : ''} skipped
            </span>
          </div>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Vendor</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Document</TableHead>
            <TableHead>Drata</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.results.map((vendorResult) =>
            vendorResult.status === 'skipped' ? (
              <SkippedVendorRow key={vendorResult.vendorId} result={vendorResult} />
            ) : (
              vendorResult.clientResults.map((clientResult, i) => (
                <ClientResultRow
                  key={`${vendorResult.vendorId}-${i}`}
                  vendorName={vendorResult.vendorName}
                  showVendor={i === 0}
                  rowSpan={vendorResult.clientResults.length}
                  result={clientResult}
                />
              ))
            )
          )}
        </TableBody>
      </Table>
    </>
  )
}

function SkippedVendorRow({ result }: { result: VendorUploadResult }) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <SkipForward className="h-4 w-4 text-amber-500" />
          <span className="font-medium">{result.vendorName}</span>
        </div>
      </TableCell>
      <TableCell colSpan={3}>
        <span className="text-sm text-muted-foreground">{result.error || 'Skipped'}</span>
      </TableCell>
    </TableRow>
  )
}

function ClientResultRow({
  vendorName,
  showVendor,
  rowSpan,
  result,
}: {
  vendorName: string
  showVendor: boolean
  rowSpan: number
  result: DocumentGenerationResult
}) {
  const statusIcon =
    result.status === 'success' ? (
      <CheckCircle className="h-4 w-4 text-emerald-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    )

  const drataStatus = () => {
    if (result.status === 'failed') {
      return <span className="text-muted-foreground">-</span>
    }
    if (!result.drataRequested) {
      return <span className="text-muted-foreground text-xs">Not requested</span>
    }
    if (!result.clientHasDrataKey) {
      return <span className="text-muted-foreground text-xs">No API key</span>
    }
    if (result.drataUploaded) {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
          <CheckCircle className="h-3 w-3" />
          Uploaded
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-xs">
        <AlertCircle className="h-3 w-3" />
        {result.drataError ? DRATA_UPLOAD_ERROR_LABELS[result.drataError] : 'Failed'}
      </span>
    )
  }

  return (
    <TableRow>
      {showVendor && (
        <TableCell rowSpan={rowSpan} className="align-top font-medium">
          {vendorName}
        </TableCell>
      )}
      <TableCell>
        <div className="flex items-center gap-2">
          {statusIcon}
          <span>{result.clientName}</span>
        </div>
        {result.error && <p className="text-xs text-red-600 mt-1">{result.error}</p>}
      </TableCell>
      <TableCell>
        {result.googleDriveId ? (
          <a
            href={`https://docs.google.com/document/d/${result.googleDriveId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline text-sm"
          >
            View Doc
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>{drataStatus()}</TableCell>
    </TableRow>
  )
}
